/**
 * «Orden de los contactos en Pendientes» (dirección, 16/10/2026 y 31/10/2026),
 * desde Campañas. Antes, «Prioridad de los contactos».
 *
 * Pendientes sale por NIVELES: primero los del nivel 1, luego los del 2… y los
 * que no están en ninguno, al final; dentro de cada nivel, el orden de
 * siempre. Cada nivel tiene un nombre (lo que dice el distintivo en Leads) y
 * sus reglas (components/ReglasContactos.jsx): un contacto está en el PRIMER
 * nivel cuyas reglas cumple, para su campaña o para todas. No se oculta a
 * nadie. El «Prioridad alta» de antes es el nivel 1.
 *
 * Las reglas son DATOS de la clínica (las guarda el servidor,
 * services/prioridad_leads.py): aquí no hay ninguna escrita. Solo
 * administradores (el servidor contesta 403 a los demás).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDateTime } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from 'lucide-react';
import {
  EditorRegla, copiaReglas, mensajeError, problemaDeLasReglas, reglaNueva,
} from './ReglasContactos';

const copiaNiveles = (niveles) => (niveles || []).map((n) => ({ ...n, reglas: copiaReglas(n.reglas) }));

/** Lo que impide guardar los niveles, en cristiano; '' si se puede. */
export function problemaDeNiveles(niveles) {
  const nombres = new Set();
  for (let i = 0; i < niveles.length; i += 1) {
    const n = niveles[i];
    const nombre = (n.nombre || '').trim();
    if (!nombre) return `Nivel ${i + 1}: ponle un nombre (es lo que dice el distintivo en Leads).`;
    if (nombres.has(nombre.toLowerCase())) return `Hay dos niveles que se llaman «${nombre}».`;
    nombres.add(nombre.toLowerCase());
    const problema = problemaDeLasReglas(n.reglas, `Nivel ${i + 1} («${nombre}»), `);
    if (problema) return problema;
  }
  return '';
}

export default function PrioridadContactosDialog({ onClose }) {
  const [datos, setDatos] = useState(null);
  const [fallo, setFallo] = useState('');
  const [niveles, setNiveles] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api.get('/api/campanas/prioridad')
      .then((r) => { if (vivo) { setDatos(r.data || {}); setNiveles(copiaNiveles(r.data?.niveles)); } })
      .catch((e) => { if (vivo) setFallo(mensajeError(e, 'No se ha podido leer el orden')); });
    return () => { vivo = false; };
  }, []);

  const campos = useMemo(() => datos?.campos || [], [datos]);
  const procedencias = datos?.procedencias || [];
  const problema = problemaDeNiveles(niveles);

  const cambiar = (fn) => setNiveles((antes) => { const nuevos = copiaNiveles(antes); fn(nuevos); return nuevos; });
  const mover = (i, d) => cambiar((ns) => { const [n] = ns.splice(i, 1); ns.splice(i + d, 0, n); });

  const guardar = async () => {
    if (guardando || problema) return;
    setGuardando(true);
    try {
      // Con la versión que se leyó: si otra persona lo ha cambiado entre
      // medias, el servidor contesta 409 y no se pisa nada.
      const r = await api.put('/api/campanas/prioridad',
        { niveles, actualizado_at: datos?.actualizado?.at ?? null });
      setDatos(r.data || {});
      setNiveles(copiaNiveles(r.data?.niveles));
      toast.success(r.data?.sin_cambios ? 'No había nada que cambiar'
        : 'Orden guardado: vale ya para todos los contactos');
      onClose();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar el orden'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(abierta) => { if (!abierta) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="prioridad-dialogo">
        <DialogHeader><DialogTitle>Orden de los contactos en Pendientes</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Leads → Pendientes sale por niveles: primero los del nivel 1, luego los del 2… y los que no
          están en ninguno, al final; dentro de cada nivel, el orden de siempre. Un contacto está en
          el <strong>primer</strong> nivel cuyas reglas cumple, y lleva su nombre como distintivo. Dentro de
          una regla se tienen que cumplir <strong>todas</strong> sus condiciones; en cada condición vale
          {' '}<strong>cualquiera</strong> de los valores marcados; «(sin respuesta)» es que no lo dice (su
          formulario no lo pregunta, o llegó por la web, el correo o a mano). No se oculta a nadie.
        </p>
        {fallo ? (
          <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3"
            data-testid="prioridad-fallo">{fallo}</div>
        ) : !datos ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="space-y-4">
            {!niveles.length ? (
              <p className="text-sm text-muted-foreground" data-testid="prioridad-sin-niveles">
                Sin niveles: Pendientes sale en el orden de siempre.
              </p>
            ) : null}
            {niveles.map((n, i) => (
              <section key={n.id || `nuevo-${i}`} className="rounded-md border p-3 space-y-2"
                data-testid={`prioridad-nivel-${i}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-sm">Nivel {i + 1}</span>
                  <Input value={n.nombre || ''} maxLength={60} className="h-8 max-w-xs text-sm"
                    placeholder="Nombre (lo que dice el distintivo)" data-testid={`prioridad-nivel-nombre-${i}`}
                    onChange={(e) => cambiar((ns) => { ns[i].nombre = e.target.value; })} />
                  <div className="ml-auto flex items-center gap-1">
                    <Button type="button" size="sm" variant="ghost" disabled={i === 0} title="Subir"
                      onClick={() => mover(i, -1)} data-testid={`prioridad-nivel-subir-${i}`}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" disabled={i === niveles.length - 1}
                      title="Bajar" onClick={() => mover(i, 1)} data-testid={`prioridad-nivel-bajar-${i}`}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost" title="Quitar este nivel"
                      onClick={() => cambiar((ns) => { ns.splice(i, 1); })} data-testid={`prioridad-nivel-quitar-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {!n.reglas.length ? (
                  <p className="text-xs text-muted-foreground">Sin reglas: nadie está en este nivel.</p>
                ) : null}
                {n.reglas.map((r, j) => (
                  <EditorRegla key={r.id || `nueva-${j}`} regla={r} campos={campos} procedencias={procedencias}
                    base={`prioridad-regla-${i}-${j}`} titulo={j === 0 ? `Regla ${j + 1}` : `O bien, regla ${j + 1}`}
                    onChange={(nueva) => cambiar((ns) => { ns[i].reglas[j] = nueva; })}
                    onQuitar={() => cambiar((ns) => { ns[i].reglas.splice(j, 1); })} />
                ))}
                <Button type="button" size="sm" variant="outline" data-testid={`prioridad-anadir-regla-${i}`}
                  onClick={() => cambiar((ns) => { ns[i].reglas.push(reglaNueva(campos)); })}>
                  <Plus className="w-4 h-4 mr-1" /> Añadir regla
                </Button>
              </section>
            ))}
            <Button type="button" size="sm" variant="outline" data-testid="prioridad-anadir-nivel"
              onClick={() => cambiar((ns) => { ns.push({ nombre: '', reglas: [reglaNueva(campos)] }); })}>
              <Plus className="w-4 h-4 mr-1" /> Añadir nivel
            </Button>
          </div>
        )}
        {datos?.actualizado?.at ? (
          <p className="text-xs text-muted-foreground" data-testid="prioridad-actualizado">
            Cambiado por última vez el {formatDateTime(datos.actualizado.at)}
            {datos.actualizado.por_nombre ? ` por ${datos.actualizado.por_nombre}` : ''}.
          </p>
        ) : null}
        {problema ? (
          <p className="text-xs text-amber-800" data-testid="prioridad-problema">{problema}</p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={!datos || guardando || !!problema || !!fallo}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="prioridad-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
